// src/email/api/client.js
import axios from 'axios';

// Master switch: Set to true to temporarily disable all Email module API calls,
// background polling, and websocket connections. To re-enable, set to false.
export const EMAIL_APIS_DISABLED = true;

export const STORAGE_KEY = 'crm_email_auth';

export const EMAIL_API_URL =
  process.env.REACT_APP_EMAIL_API_URL && !process.env.REACT_APP_EMAIL_API_URL.includes(':3000')
    ? process.env.REACT_APP_EMAIL_API_URL
    : 'http://localhost:5001';

if (EMAIL_APIS_DISABLED) {
  console.log('[Email Module] API calls are temporarily DISABLED (EMAIL_APIS_DISABLED = true).');
} else {
  console.log('[Email Module] Using backend EMAIL_API_URL:', EMAIL_API_URL);
}

export const emailApiClient = axios.create({
  baseURL: EMAIL_API_URL,
  ...(EMAIL_APIS_DISABLED
    ? {
        adapter: async (config) => {
          console.warn(`[Email Module] API call suppressed (APIs temporarily disabled): ${config.method?.toUpperCase()} ${config.url}`);
          let data = [];
          if (config.url?.includes('/lookup')) {
            data = { items: [], total: 0, hasMore: false };
          } else if (config.url?.includes('/threads/') || config.url?.includes('/drafts/')) {
            data = null;
          } else if (config.url?.includes('/threads')) {
            data = { items: [], nextCursor: null, hasMore: false };
          } else if (config.url?.includes('/accounts')) {
            data = [];
          } else {
            data = {};
          }
          return {
            data,
            status: 200,
            statusText: 'OK (APIs Disabled)',
            headers: {},
            config,
          };
        },
      }
    : {}),
});

/**
 * Headers identifying the logged-in CRM user, sent with every email backend call
 * so the backend can track which CRM user connected / used a mailbox.
 * Name is URI-encoded because header values must be ASCII.
 */
export function getCrmUserHeaders() {
  const current = getCurrentCrmUser();
  if (!current) return {};
  return {
    ...(current.id != null ? { 'X-CRM-User-Id': String(current.id) } : {}),
    'X-CRM-User-Email': current.email,
    'X-CRM-User-Name': encodeURIComponent(current.name || ''),
  };
}

emailApiClient.interceptors.request.use((config) => {
  Object.entries(getCrmUserHeaders()).forEach(([key, value]) => {
    config.headers[key] = value;
  });

  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      const { token } = JSON.parse(raw);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      console.error('Failed to parse email auth token:', e);
    }
  }
  return config;
});

emailApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem(STORAGE_KEY);
    }
    return Promise.reject(error);
  }
);

/**
 * Extracts and normalizes the logged-in CRM user from localStorage ('user').
 * CRM user records typically use `office_email`, `user_email`, `user_fullname`, `user_name`, and `id`.
 */
export function getCurrentCrmUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (!u) return null;

    let email =
      (u.office_email && typeof u.office_email === 'string' && u.office_email.trim()) ||
      (u.user_email && typeof u.user_email === 'string' && u.user_email.trim()) ||
      (u.email && typeof u.email === 'string' && u.email.trim());

    if (!email && u.id) {
      email = `crm_user_${u.id}@streetgains.in`;
    } else if (!email && u.user_name) {
      email = `${u.user_name.replace(/[^a-zA-Z0-9_.-]/g, '').toLowerCase()}@streetgains.in`;
    }

    let name =
      (u.user_fullname && typeof u.user_fullname === 'string' && u.user_fullname.trim()) ||
      (u.name && typeof u.name === 'string' && u.name.trim()) ||
      (u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : '') ||
      (u.user_name && typeof u.user_name === 'string' && u.user_name.trim()) ||
      (u.username && typeof u.username === 'string' && u.username.trim()) ||
      (u.id ? `User ${u.id}` : 'CRM Executive');

    if (!email) return null;

    return {
      id: u.id,
      email: email.toLowerCase(),
      name,
    };
  } catch (e) {
    console.error('[Email Auth] Error parsing CRM user from localStorage:', e);
    return null;
  }
}

export function getStoredEmailToken() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const current = getCurrentCrmUser();

    if (!current) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Invalidate cached token if token is missing or user email does not match current CRM user
    if (
      !parsed?.token ||
      !parsed?.user?.email ||
      parsed.user.email.toLowerCase() !== current.email.toLowerCase() || (current.id != null && String(parsed?.user?.id) !== String(current.id))
    ) {
      console.log(
        `[Email Auth] CRM user session mismatch (cached: ${parsed?.user?.email || 'none'} vs current: ${current.email}). Invalidating email session.`
      );
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.token;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Ensures the email backend has a valid JWT token matching the currently logged-in CRM user.
 */
export async function ensureEmailAuth() {
  if (EMAIL_APIS_DISABLED) {
    return null;
  }
  const current = getCurrentCrmUser();
  if (!current) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }

  const existingToken = getStoredEmailToken();
  if (existingToken) {
    return existingToken;
  }

  try {
    console.log(
      `[Email Auth] Authenticating email session for CRM user: ${current.name} (${current.email})`
    );
    const res = await axios.post(`${EMAIL_API_URL}/auth/dev-login`, {
      userId: current.id != null ? String(current.id) : undefined,
      email: current.email,
      name: current.name,
    });

    if (res.data?.accessToken) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          token: res.data.accessToken,
          user: res.data.user,
        })
      );
      // Notify components / sidebar that email auth is ready for this user
      window.dispatchEvent(new CustomEvent('emailAuthReady'));
      return res.data.accessToken;
    }
  } catch (err) {
    console.error('[Email Auth] Failed to authenticate with Email service:', err);
  }
  return null;
}
